import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { applicationsAPI, collegesAPI } from '../utils/api';
import PersonalInfoForm from '../components/application/PersonalInfoForm';
import FamilyForm from '../components/application/FamilyForm';
import EducationForm from '../components/application/EducationForm';
import CoursesForm from '../components/application/CoursesForm';
import TestingForm from '../components/application/TestingForm';
import ActivitiesForm from '../components/application/ActivitiesForm';
import HonorsForm from '../components/application/HonorsForm';
import EssaysForm from '../components/application/EssaysForm';
import RecommendersForm from '../components/application/RecommendersForm';
import DocumentsForm from '../components/application/DocumentsForm';
import AdditionalInfoForm from '../components/application/AdditionalInfoForm';
import PaymentForm from '../components/application/PaymentForm';
import SupplementalEssaysForm from '../components/application/SupplementalEssaysForm';
import FerpaWaiverForm from '../components/application/FerpaWaiverForm';
import { CheckCircle, Circle, CreditCard, FileText, Shield, Send, Eye, GraduationCap, ChevronDown, Download, Loader2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const Application = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const applicationId = searchParams.get('app_id');
  const initialSection = searchParams.get('section') || 'personal';
  const [activeSection, setActiveSection] = useState(initialSection);
  const [applicationData, setApplicationData] = useState(null);
  const [allApplications, setAllApplications] = useState([]);
  const [colleges, setColleges] = useState({});
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    loadAllApplications();
  }, []);

  useEffect(() => {
    if (applicationId && allApplications.length > 0) {
      const app = allApplications.find(a => a._id === applicationId);
      if (app) {
        setApplicationData(app);
      }
    }
  }, [applicationId, allApplications]);

  const loadAllApplications = async () => {
    try {
      const apps = await applicationsAPI.getAll();
      setAllApplications(apps);
      
      // Load college names for all applications
      const collegeMap = {};
      for (const app of apps) {
        if (app.college_id && !collegeMap[app.college_id]) {
          try {
            const college = await collegesAPI.getById(app.college_id);
            collegeMap[app.college_id] = college;
          } catch {
            collegeMap[app.college_id] = { name: 'Unknown College' };
          }
        }
      }
      setColleges(collegeMap);
      
      // Set initial application
      if (apps.length > 0) {
        if (applicationId) {
          const app = apps.find(a => a._id === applicationId);
          setApplicationData(app || apps[0]);
        } else {
          setApplicationData(apps[0]);
        }
      }
    } catch (error) {
      console.error('Error loading applications:', error);
      toast({
        title: 'Error',
        description: 'Failed to load applications',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApplicationChange = (appId) => {
    const app = allApplications.find(a => a._id === appId);
    if (app) {
      setApplicationData(app);
      navigate(`/application?app_id=${appId}`);
    }
  };

  const getCollegeName = (collegeId) => {
    return colleges[collegeId]?.name || 'Unknown College';
  };

  const sections = [
    { id: 'personal_info', label: 'Personal Info', component: PersonalInfoForm },
    { id: 'family_info', label: 'Family', component: FamilyForm },
    { id: 'education', label: 'Education', component: EducationForm },
    { id: 'courses', label: 'Courses & Grades', component: CoursesForm },
    { id: 'testing', label: 'Testing', component: TestingForm },
    { id: 'activities', label: 'Activities', component: ActivitiesForm },
    { id: 'honors', label: 'Honors & Awards', component: HonorsForm },
    { id: 'essays', label: 'Essays', component: EssaysForm },
    { id: 'supplemental', label: 'Supplemental Essays', component: SupplementalEssaysForm, needsAppId: true, needsCollegeId: true },
    { id: 'recommenders', label: 'Recommenders', component: RecommendersForm, needsAppId: true },
    { id: 'ferpa', label: 'FERPA Waiver', component: FerpaWaiverForm },
    { id: 'documents', label: 'Documents', component: DocumentsForm, needsAppId: true },
    { id: 'additional_info', label: 'Additional Info', component: AdditionalInfoForm },
    { id: 'payment', label: 'Payment', component: PaymentForm, needsAppId: true, needsCollegeName: true }
  ];

  const isSectionComplete = (sectionId) => {
    if (!applicationData) return false;
    const data = applicationData[sectionId];
    if (!data) return false;
    
    if (sectionId === 'essays') {
      return data.common_app_essay && data.common_app_essay.content && data.common_app_essay.content.length > 100;
    }
    
    if (Array.isArray(data)) {
      return data.length > 0;
    }
    
    return Object.keys(data).length > 0;
  };

  const calculateProgress = () => {
    if (!applicationData) return 0;
    return applicationData.progress || 0;
  };

  const handleSave = async (sectionId, data) => {
    if (!applicationData) return;
    
    try {
      const updateData = { [sectionId]: data };
      const updated = await applicationsAPI.update(applicationData._id, updateData);
      setApplicationData(updated);
      toast({
        title: 'Saved',
        description: 'Your changes have been saved',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save changes',
        variant: 'destructive',
      });
    }
  };

  const downloadProfile = () => {
    if (!applicationData) return;
    
    setDownloading(true);
    
    try {
      // Create a formatted text version of the profile
      const collegeName = getCollegeName(applicationData.college_id);
      const personalInfo = applicationData.personal_info || {};
      const familyInfo = applicationData.family_info || {};
      const education = applicationData.education || {};
      const testing = applicationData.testing || {};
      const activities = applicationData.activities || [];
      const honors = applicationData.honors || [];
      const essays = applicationData.essays || {};
      
      const profileContent = `
================================================================================
                        WAYO APPLICATION PROFILE
================================================================================

Application for: ${collegeName}
Generated on: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}

--------------------------------------------------------------------------------
                           PERSONAL INFORMATION
--------------------------------------------------------------------------------
First Name: ${personalInfo.firstName || 'N/A'}
Middle Name: ${personalInfo.middleName || 'N/A'}
Last Name: ${personalInfo.lastName || 'N/A'}
Preferred Name: ${personalInfo.preferredName || 'N/A'}
Date of Birth: ${personalInfo.dateOfBirth || 'N/A'}
Gender: ${personalInfo.gender || 'N/A'}
Email: ${personalInfo.email || user?.email || 'N/A'}
Phone: ${personalInfo.phone || 'N/A'}

Address:
${personalInfo.address || 'N/A'}
${personalInfo.city || ''}, ${personalInfo.state || ''} ${personalInfo.zipCode || ''}
${personalInfo.country || 'N/A'}

Citizenship: ${personalInfo.citizenship || 'N/A'}
Visa Status: ${personalInfo.visaStatus || 'N/A'}

--------------------------------------------------------------------------------
                           FAMILY INFORMATION
--------------------------------------------------------------------------------
Parent 1:
  Name: ${familyInfo.parent1Name || 'N/A'}
  Occupation: ${familyInfo.parent1Occupation || 'N/A'}
  Education: ${familyInfo.parent1Education || 'N/A'}

Parent 2:
  Name: ${familyInfo.parent2Name || 'N/A'}
  Occupation: ${familyInfo.parent2Occupation || 'N/A'}
  Education: ${familyInfo.parent2Education || 'N/A'}

Number of Siblings: ${familyInfo.siblings || 'N/A'}

--------------------------------------------------------------------------------
                           EDUCATION
--------------------------------------------------------------------------------
High School: ${education.schoolName || 'N/A'}
Location: ${education.schoolCity || ''}, ${education.schoolState || ''}, ${education.schoolCountry || ''}
Graduation Year: ${education.graduationYear || 'N/A'}
GPA: ${education.gpa || 'N/A'} / ${education.gpaScale || '4.0'}
Class Rank: ${education.classRank || 'N/A'} of ${education.classSize || 'N/A'}

--------------------------------------------------------------------------------
                           TEST SCORES
--------------------------------------------------------------------------------
SAT Score: ${testing.satTotal || 'N/A'}
  - Math: ${testing.satMath || 'N/A'}
  - Reading/Writing: ${testing.satReading || 'N/A'}

ACT Composite: ${testing.actComposite || 'N/A'}
  - English: ${testing.actEnglish || 'N/A'}
  - Math: ${testing.actMath || 'N/A'}
  - Reading: ${testing.actReading || 'N/A'}
  - Science: ${testing.actScience || 'N/A'}

TOEFL Score: ${testing.toefl || 'N/A'}
IELTS Score: ${testing.ielts || 'N/A'}

--------------------------------------------------------------------------------
                           ACTIVITIES
--------------------------------------------------------------------------------
${activities.length > 0 ? activities.map((act, i) => `
${i + 1}. ${act.name || 'Activity'}
   Position: ${act.position || 'N/A'}
   Grade Levels: ${act.gradeLevels || 'N/A'}
   Hours/Week: ${act.hoursPerWeek || 'N/A'}
   Weeks/Year: ${act.weeksPerYear || 'N/A'}
   Description: ${act.description || 'N/A'}
`).join('\n') : 'No activities listed.'}

--------------------------------------------------------------------------------
                           HONORS & AWARDS
--------------------------------------------------------------------------------
${honors.length > 0 ? honors.map((honor, i) => `
${i + 1}. ${honor.title || 'Honor'}
   Level: ${honor.level || 'N/A'}
   Grade Level: ${honor.gradeLevel || 'N/A'}
   Description: ${honor.description || 'N/A'}
`).join('\n') : 'No honors or awards listed.'}

--------------------------------------------------------------------------------
                           ESSAYS
--------------------------------------------------------------------------------
Common App Essay Topic: ${essays.common_app_essay?.topic || 'N/A'}

Essay Content:
${essays.common_app_essay?.content || 'Not yet written.'}

--------------------------------------------------------------------------------
                           APPLICATION STATUS
--------------------------------------------------------------------------------
Progress: ${applicationData.progress || 0}%
Status: ${applicationData.status || 'In Progress'}

================================================================================
         This profile was generated by Wayo - Your Way Forward
                        www.wayoedu.com
================================================================================
`;

      // Create and download the file
      const blob = new Blob([profileContent], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Wayo_Profile_${collegeName.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast({
        title: 'Profile Downloaded',
        description: 'Your application profile has been downloaded successfully.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to download profile',
        variant: 'destructive',
      });
    } finally {
      setDownloading(false);
    }
  };

  const progress = calculateProgress();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex items-center justify-center">
        <div className="text-xl text-gray-700">Loading application...</div>
      </div>
    );
  }

  if (!applicationData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>No Application Found</CardTitle>
            <CardDescription>Please add a college to start an application</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => window.location.href = '/colleges'}>Browse Colleges</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">College Application</h1>
              <p className="text-gray-600">Complete your application to apply to colleges</p>
            </div>
            
            {/* Application Selector */}
            {allApplications.length > 1 && (
              <div className="flex items-center gap-3">
                <GraduationCap className="h-5 w-5 text-emerald-600" />
                <Select 
                  value={applicationData?._id} 
                  onValueChange={handleApplicationChange}
                >
                  <SelectTrigger className="w-[280px] bg-white">
                    <SelectValue placeholder="Select College Application" />
                  </SelectTrigger>
                  <SelectContent>
                    {allApplications.map(app => (
                      <SelectItem key={app._id} value={app._id}>
                        <div className="flex items-center gap-2">
                          <span>{getCollegeName(app.college_id)}</span>
                          <span className="text-xs text-gray-500">
                            ({app.progress || 0}%)
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          
          {/* Current Application Info */}
          {applicationData && (
            <div className="mt-4 p-4 bg-white rounded-lg border border-emerald-100 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="bg-emerald-100 p-2 rounded-lg">
                  <GraduationCap className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">
                    {getCollegeName(applicationData.college_id)}
                  </p>
                  <p className="text-sm text-gray-500">
                    Status: {applicationData.status === 'submitted' ? 'Submitted' : 'In Progress'} • 
                    {applicationData.deadline_type || 'RD'} Application
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <Card className="sticky top-20">
              <CardHeader>
                <CardTitle className="text-lg">Application Progress</CardTitle>
                <div className="mt-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span>Overall</span>
                    <span className="font-semibold">{progress}%</span>
                  </div>
                  <Progress value={progress} className="h-2" />
                </div>
              </CardHeader>
              <CardContent>
                <nav className="space-y-1">
                  {sections.map(section => (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-left transition-colors ${
                        activeSection === section.id
                          ? 'bg-blue-50 text-blue-700 font-medium'
                          : 'hover:bg-gray-50 text-gray-700'
                      }`}
                    >
                      <span>{section.label}</span>
                      {isSectionComplete(section.id) ? (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      ) : (
                        <Circle className="h-5 w-5 text-gray-300" />
                      )}
                    </button>
                  ))}
                </nav>
                
                {/* Review & Submit Button */}
                <div className="mt-6 pt-4 border-t border-gray-200 space-y-3">
                  <Link to={`/application/${applicationData._id}/review`}>
                    <Button className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700">
                      <Eye className="h-4 w-4 mr-2" />
                      Review & Submit
                    </Button>
                  </Link>
                  
                  {/* Download Profile Button */}
                  <Button 
                    variant="outline" 
                    className="w-full border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                    onClick={downloadProfile}
                    disabled={downloading}
                    data-testid="download-profile-btn"
                  >
                    {downloading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    Download Profile
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {sections.map(section => {
              const Component = section.component;
              return (
                activeSection === section.id && (
                  <Card key={section.id}>
                    <CardHeader>
                      <CardTitle>{section.label}</CardTitle>
                      <CardDescription>
                        {section.id === 'personal' && 'Provide your basic personal information'}
                        {section.id === 'family' && 'Tell us about your family background'}
                        {section.id === 'education' && 'List your educational history'}
                        {section.id === 'testing' && 'Add your standardized test scores'}
                        {section.id === 'activities' && 'Describe your extracurricular activities'}
                        {section.id === 'essays' && 'Write your application essays'}
                        {section.id === 'supplemental' && 'College-specific essay questions'}
                        {section.id === 'ferpa' && 'Recommendation letter access rights'}
                        {section.id === 'payment' && 'Pay your application fee'}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Component
                        data={applicationData[section.id] || {}}
                        onSave={(data) => handleSave(section.id, data)}
                        {...(section.needsAppId && { applicationId: applicationData._id })}
                        {...(section.needsCollegeId && { collegeId: applicationData.college_id })}
                        {...(section.needsCollegeName && { collegeName: applicationData.college_name })}
                      />
                    </CardContent>
                  </Card>
                )
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Application;
